import { useState, useCallback, useEffect, useRef } from "react";
import { Button, Input } from "antd";
import { SearchOutlined, UpOutlined, DownOutlined } from "@ant-design/icons";
import Mark from "mark.js";
import styled from "styled-components";

const EditorSearch = () => {
  const [searchValue, setSearchValue] = useState("");
  const [searchCount, setSearchCount] = useState(0);
  const [searchCurrentIndex, setSearchCurrentIndex] = useState(0);
  const markInstanceRef = useRef<Mark | null>(null);

  const scrollToMark = useCallback((index: number) => {
    const marks = document.querySelectorAll("#editorJS mark");
    if (marks[index]) {
      marks[index].scrollIntoView({ behavior: "smooth", block: "center" });
      marks.forEach((m, i) => {
        (m as HTMLElement).style.backgroundColor = i === index ? "#ff9800" : "#fff176";
      });
    }
  }, []);

  const getMarkInstance = useCallback(() => {
    if (markInstanceRef.current) return markInstanceRef.current;
    const editorEl = document.getElementById("editorJS");
    if (!editorEl) return null;
    const instance = new Mark(editorEl);
    markInstanceRef.current = instance;
    return instance;
  }, []);

  const executeSearch = useCallback((keyword: string) => {
    const markInstance = getMarkInstance();
    if (!markInstance) return;

    markInstance.unmark();

    if (!keyword.trim()) {
      setSearchCount(0);
      setSearchCurrentIndex(0);
      return;
    }

    markInstance.mark(keyword.trim(), {
      separateWordSearch: false,
      each: (node: HTMLElement) => {
        node.style.backgroundColor = "#fff176";
        node.style.borderRadius = "2px";
        node.style.padding = "0 1px";
      },
      done: (count: number) => {
        setSearchCount(count);
        setSearchCurrentIndex(0);
        if (count > 0) {
          setTimeout(() => scrollToMark(0), 0);
        }
      },
    });
  }, [getMarkInstance, scrollToMark]);

  const handleSearchPrev = useCallback(() => {
    if (searchCount === 0) return;
    const newIndex = searchCurrentIndex === 0 ? searchCount - 1 : searchCurrentIndex - 1;
    setSearchCurrentIndex(newIndex);
    scrollToMark(newIndex);
  }, [searchCount, searchCurrentIndex, scrollToMark]);

  const handleSearchNext = useCallback(() => {
    if (searchCount === 0) return;
    const newIndex = searchCurrentIndex === searchCount - 1 ? 0 : searchCurrentIndex + 1;
    setSearchCurrentIndex(newIndex);
    scrollToMark(newIndex);
  }, [searchCount, searchCurrentIndex, scrollToMark]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (searchCount > 0) {
        handleSearchNext();
      } else {
        executeSearch(searchValue);
      }
    }
  }, [searchValue, searchCount, handleSearchNext, executeSearch]);

  // debounce 검색
  useEffect(() => {
    const timer = setTimeout(() => {
      executeSearch(searchValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, executeSearch]);

  // unmount 시 마킹 해제
  useEffect(() => {
    return () => {
      if (markInstanceRef.current) {
        markInstanceRef.current.unmark();
      }
    };
  }, []);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <EditorSearchInput
        size="small"
        prefix={<SearchOutlined />}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search in content..."
      />
      {searchCount > 0 ? (
        <>
          <span style={{ fontSize: "12px", color: "#595959", flexShrink: 0, whiteSpace: "nowrap" }}>
            {searchCurrentIndex + 1}/{searchCount}
          </span>
          <WordNavButton
            type="text"
            size="small"
            icon={<UpOutlined />}
            onClick={handleSearchPrev}
            disabled={searchCount === 0}
          />
          <WordNavButton
            type="text"
            size="small"
            icon={<DownOutlined />}
            onClick={handleSearchNext}
            disabled={searchCount === 0}
          />
        </>
      ) : (
        (searchValue && <span style={{ color: "#adadad" }}>No matched result</span>)
      )}

    </div>
  );
};

export default EditorSearch;

const EditorSearchInput = styled(Input)`
  span {
    color:#adadad
  }
  padding: 0.1rem 1rem 0.1rem 0.5rem;
  flex: 1;
  min-width: 0;
  width: 18rem;
`

const WordNavButton = styled(Button)`
  padding: "2px 4px"
`
